import React, { useEffect, useState } from "react";
import {
    Card,
    CardBody,
    CardHeader,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Spinner,
    Alert,
    Row,
    Col,
} from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../../api/urls";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PhoneInput from "../../components/Forms/PhoneInput"
import "../../assets/css/page-common.css";
import {
    accountTypeOptionsFor,
    isSoleProprietorship as isSolePropFn,
} from "../../constants/accountTypes";
import { DISTRICTS } from "../../constants/districts";
import BackButton from "../../components/Common/BackButton";

const EditAccountPage = () => {
    const { id: accountId } = useParams();
    const navigate = useNavigate();

    const [companies, setCompanies] = useState([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [groups, setGroups] = useState([]);
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [loading, setLoading] = useState(true);
    const [districtOptions, setDistrictOptions] = useState([]);
    const showToast = (type, msg) => {
        const map = { danger: "error", error: "error" };
        const toastFunc = toast[map[type] || type] || toast.info;
        toastFunc(msg, { autoClose: 3000 });
    };

    const [form, setForm] = useState({
        accountName: "",
        accountCode: "",
        title: "",
        taxOffice: "",
        nationalId: "",
        vknNumber: "",
        phoneNumber: "",
        country: "Türkiye",
        city: "",
        district: "",
        address: "",
        description: "",
        accountType: "SOLE_PROPRIETORSHIP",
        companyId: "",
        accountGroupId: "",
        enabled: true,
    });

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (accountId) {
            fetchAccountDetails();
            fetchCompanies();
        }
    }, [accountId]);

    useEffect(() => {
        if (form.companyId) {
            fetchGroups(form.companyId);
        } else {
            setGroups([]);
        }
    }, [form.companyId]);

    const toTitleCase = (str) => {
        return str.toLowerCase().split(' ').map(function(word) {
            if (!word) return ''; // Boşlukları ele
            return word.charAt(0).toUpperCase() + word.slice(1);
        }).join(' ');
    };

    // Cari adı için özel değişim işleyici (Title Case uygular)
    const handleAccountNameChange = (e) => {
        const { name, value } = e.target;
        const convertedValue = toTitleCase(value);
        setForm((prev) => ({ ...prev, [name]: convertedValue }));

        setError(null);
        setSuccess(false);
    };

    const fetchAccountDetails = async () => {
        setLoading(true);
        try {
            const res = await axios.get(API.account(accountId));
            const account = res.data.data;
            setForm({
                accountName: account.accountName || "",
                accountCode: account.accountCode || "",
                title: account.title || "",
                taxOffice: account.taxOffice || "",
                nationalId: account.nationalId || "",
                vknNumber: account.vknNumber || "",
                phoneNumber: account.phoneNumber || "",
                country: account.country || "Türkiye",
                city: account.city || "",
                district: account.district || "",
                address: account.address || "",
                description: account.description || "",
                accountType: account.accountType || "SOLE_PROPRIETORSHIP",
                companyId: account.companyId || "",
                accountGroupId: account.accountGroupId || "",
                enabled: account.enabled !== undefined ? account.enabled : true,
            });
            if (account.city) {
                setDistrictOptions(DISTRICTS[account.city] || []);
            }
        } catch (err) {
            console.error("Cari detayları yüklenirken hata:", err);
            setError("Cari bilgileri yüklenemedi");
        } finally {
            setLoading(false);
        }
    };

    const fetchCompanies = async () => {
        setLoadingCompanies(true);
        try {
            const response = await axios.get(API.get_companies_names);
            setCompanies(response.data.data || []);
        } catch (err) {
            console.error("Firmalar yüklenirken hata:", err);
            setCompanies([]);
        } finally {
            setLoadingCompanies(false);
        }
    };

    const fetchGroups = async (companyId) => {
        setLoadingGroups(true);
        try {
            const res = await axios.get(API.groups_by_company(companyId));
            setGroups(res.data.data || []);
        } catch {
            setGroups([]);
        } finally {
            setLoadingGroups(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setError(null);
        setSuccess(false);

        if (type === "checkbox") {
            setForm((prev) => ({ ...prev, [name]: checked }));
        } else {
            if (name === "city") {
                setDistrictOptions(DISTRICTS[value] || []);
                setForm((prev) => ({
                    ...prev,
                    city: value,
                    district: "",
                }));
            } else {
                setForm((prev) => ({ ...prev, [name]: value }));
            }
        }
    };

    const handleAccountTypeChange = (e) => {
        const newAccountType = e.target.value;
        setForm((prev) => ({
            ...prev,
            accountType: newAccountType,
            nationalId: "",
            vknNumber: "",
        }));
    };

    const validateTcKimlik = (tcKimlik) => {
        if (!tcKimlik || tcKimlik.length !== 11) return false;
        if (!/^\d{11}$/.test(tcKimlik)) return false;
        if (tcKimlik[0] === '0') return false;

        const digits = tcKimlik.split('').map(Number);
        const sum1 = digits[0] + digits[2] + digits[4] + digits[6] + digits[8];
        const sum2 = digits[1] + digits[3] + digits[5] + digits[7];
        const check1 = (sum1 * 7 - sum2) % 10;
        const check2 = (sum1 + sum2 + digits[9]) % 10;

        return check1 === digits[9] && check2 === digits[10];
    };

    const validateForm = () => {
        if (!form.accountName.trim()) return "Cari/Bayi adı gereklidir";
        if (!form.accountCode.trim()) return "Cari/Bayi kodu gereklidir";
        if (form.accountCode.length !== 6) return "Cari/Bayi kodu 6 haneli olmalıdır";
        if (!form.companyId) return "Firma seçimi gereklidir";

        const isSoleProp = isSolePropFn(form.accountType);

        if (isSoleProp) {
            if (!form.nationalId) return "Şahıs şirketi için TC Kimlik numarası gereklidir";
            if (!validateTcKimlik(form.nationalId)) return "Geçerli bir TC Kimlik numarası giriniz";
        } else {
            if (!form.vknNumber) return "Bu cari/bayi tipi için VKN gereklidir";
            if (!/^\d{10}$/.test(form.vknNumber)) return "VKN 10 haneli olmalıdır";
        }

        if (form.nationalId && !validateTcKimlik(form.nationalId)) {
            return "Geçerli bir TC Kimlik numarası giriniz";
        }
        if (form.vknNumber && !/^\d{10}$/.test(form.vknNumber)) {
            return "VKN 10 haneli olmalıdır";
        }
        if (form.phoneNumber && !/^[+]?[0-9]{7,15}$/.test(form.phoneNumber)) {
            return "Geçerli bir telefon numarası giriniz";
        }

        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        setSuccess(false);

        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            setSaving(false);
            return;
        }

        try {
            const payload = {
                accountName: form.accountName.trim(),
                accountCode: form.accountCode.trim(),
                title: form.title.trim() || undefined,
                taxOffice: form.taxOffice.trim() || undefined,
                nationalId: form.nationalId.trim() || undefined,
                vknNumber: form.vknNumber.trim() || undefined,
                phoneNumber: form.phoneNumber.trim() || undefined,
                country: form.country.trim() || undefined,
                city: form.city.trim() || undefined,
                district: form.district.trim() || undefined,
                address: form.address.trim() || undefined,
                description: form.description.trim() || undefined,
                accountType: form.accountType,
                companyId: form.companyId,
                accountGroupId: form.accountGroupId || undefined,
                enabled: form.enabled,
            };

            await axios.put(API.account(accountId), payload, {
                headers: {
                    "Content-Type": "application/json",
                }
            });

            showToast("success", "Cari bilgileri başarıyla güncellendi!");

            setTimeout(() => {
                navigate(`/accounts/${accountId}`);
            }, 1500);

        } catch (err) {
            console.error("Güncelleme hatası:", err);
            const errorMsg = err.response?.data?.message || "Güncelleme sırasında bir hata oluştu";
            setError(errorMsg);
            showToast("error", errorMsg);
        } finally {
            setSaving(false);
        }
    };

    const turkishCities = [
        "Adana", "Adıyaman", "Afyonkarahisar", "Ağrı", "Amasya", "Ankara", "Antalya", "Artvin",
        "Aydın", "Balıkesir", "Bilecik", "Bingöl", "Bitlis", "Bolu", "Burdur", "Bursa", "Çanakkale",
        "Çankırı", "Çorum", "Denizli", "Diyarbakır", "Edirne", "Elazığ", "Erzincan", "Erzurum",
        "Eskişehir", "Gaziantep", "Giresun", "Gümüşhane", "Hakkâri", "Hatay", "Isparta", "Mersin",
        "İstanbul", "İzmir", "Kars", "Kastamonu", "Kayseri", "Kırklareli", "Kırşehir", "Kocaeli",
        "Konya", "Kütahya", "Malatya", "Manisa", "Kahramanmaraş", "Mardin", "Muğla", "Muş",
        "Nevşehir", "Niğde", "Ordu", "Rize", "Sakarya", "Samsun", "Siirt", "Sinop", "Sivas",
        "Tekirdağ", "Tokat", "Trabzon", "Tunceli", "Şanlıurfa", "Uşak", "Van", "Yozgat", "Zonguldak",
        "Aksaray", "Bayburt", "Karaman", "Kırıkkale", "Batman", "Şırnak", "Bartın", "Ardahan",
        "Iğdır", "Yalova", "Karabük", "Kilis", "Osmaniye", "Düzce"
    ];

    const isSoleProp = isSolePropFn(form.accountType);

    const NAVY = "#221C5D";

    if (loading) {
        return (
            <div className="page-content text-center p-5">
                <Spinner size="lg" color="primary" />
                <h6 className="mt-3 text-muted">Yükleniyor...</h6>
            </div>
        );
    }

    return (
        <React.Fragment>
            <div className="page-content page-common">
                <div className="container-fluid">

                    {/* ── Page Header ── */}
                    <div className="page-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-3">
                            <BackButton />
                            <div>
                                <h5 className="mb-0 fw-bold" style={{ color: "#1e293b" }}>
                                    {form.accountName || "Cari/Bayi Düzenle"}
                                </h5>
                                <span className="text-muted" style={{ fontSize: ".82rem" }}>
                                    Cari/Bayi Düzenleme Sayfası
                                </span>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <button
                                className="btn btn-navy btn-rounded d-flex align-items-center gap-1 px-3"
                                style={{ height: 34, fontSize: ".8rem" }}
                                onClick={handleSubmit}
                                disabled={saving}
                            >
                                {saving ? (<><Spinner size="sm" className="me-1" /> Kaydediliyor...</>) : (<><i className="bx bx-save" style={{ fontSize: 16 }} /> Değişiklikleri Kaydet</>)}
                            </button>
                        </div>
                    </div>

                    <Row>
                        <Col lg={12}>
                            <Card className="detail-card">
                                <CardHeader>
                                    <div className="section-title">
                                        <div className="section-icon" style={{ background: "rgba(34,28,93,.1)", color: NAVY }}>
                                            <i className="bx bx-edit-alt" />
                                        </div>
                                        Cari/Bayi Bilgileri
                                    </div>
                                </CardHeader>
                                <CardBody className="p-4">
                                    {error && (
                                        <Alert color="danger" className="border-0 rounded-4">
                                            <i className="bx bx-error me-2"></i>
                                            {error}
                                        </Alert>
                                    )}

                                    <Form onSubmit={handleSubmit}>
                                        {/* Cari Tipi ve Durum */}
                                        <div className="border rounded-4 p-4 mb-4" style={{ backgroundColor: '#f9f9f9' }}>
                                            <h6 className="mb-3 fw-semibold">Cari/Bayi Tipi ve Durum</h6>
                                            <Row>
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">Cari/Bayi Tipi</Label>
                                                        <Input
                                                            type="select"
                                                            name="accountType"
                                                            value={form.accountType}
                                                            onChange={handleAccountTypeChange}
                                                            className="rounded-3"
                                                        >
                                                            {accountTypeOptionsFor(form.accountType).map((type) => (
                                                                <option key={type.value} value={type.value}>
                                                                    {type.label}
                                                                </option>
                                                            ))}
                                                        </Input>
                                                        <small className="text-muted">
                                                            {isSoleProp
                                                                ? "Şahıs şirketi için TC Kimlik numarası zorunludur"
                                                                : "Bu cari/bayi tipi için VKN zorunludur"}
                                                        </small>
                                                    </FormGroup>
                                                </Col>
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">Hesap Durumu</Label>
                                                        <div className="d-flex align-items-center">
                                                            <div
                                                                className="toggle-switch"
                                                                onClick={() => setForm((prev) => ({ ...prev, enabled: !prev.enabled }))}
                                                                style={{
                                                                    width: "50px",
                                                                    height: "24px",
                                                                    backgroundColor: form.enabled ? "#28a745" : "#6c757d",
                                                                    borderRadius: "12px",
                                                                    position: "relative",
                                                                    cursor: "pointer",
                                                                    transition: "background-color 0.2s",
                                                                }}
                                                            >
                                                                <div
                                                                    style={{
                                                                        width: "20px",
                                                                        height: "20px",
                                                                        backgroundColor: "white",
                                                                        borderRadius: "50%",
                                                                        position: "absolute",
                                                                        top: "2px",
                                                                        left: form.enabled ? "28px" : "2px",
                                                                        transition: "left 0.2s",
                                                                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                                                    }}
                                                                />
                                                            </div>
                                                            <span className="ms-3">
                                                                <strong className="d-block">{form.enabled ? "Aktif" : "Pasif"}</strong>
                                                                <small className="text-muted">
                                                                    {form.enabled ? "Hesap kullanılabilir" : "Hesap devre dışı"}
                                                                </small>
                                                            </span>
                                                        </div>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        </div>

                                        {/* Temel Bilgiler */}
                                        <div className="border rounded-4 p-4 mb-4" style={{ backgroundColor: '#f9f9f9' }}>
                                            <h6 className="mb-3 fw-semibold">Temel Bilgiler</h6>
                                            <Row className="g-3">
                                                <Col md={8}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">Cari/Bayi Adı <span className="text-danger">*</span></Label>
                                                        <Input
                                                            name="accountName"
                                                            value={form.accountName}
                                                            onChange={handleAccountNameChange}
                                                            maxLength={150}
                                                            className="rounded-3"
                                                            placeholder="Cari/Bayi hesap adını girin"
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col md={4}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">Cari/Bayi Kodu <span className="text-danger">*</span></Label>
                                                        <Input
                                                            name="accountCode"
                                                            value={form.accountCode}
                                                            onChange={handleChange}
                                                            maxLength={6}
                                                            className="rounded-3"
                                                            placeholder="6 haneli kod"
                                                            disabled
                                                        />
                                                        <small className="text-muted">Kod değiştirilemez</small>
                                                    </FormGroup>
                                                </Col>
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">Firma <span className="text-danger">*</span></Label>
                                                        <Input
                                                            type="select"
                                                            name="companyId"
                                                            value={form.companyId}
                                                            onChange={handleChange}
                                                            className="rounded-3"
                                                            disabled={loadingCompanies}
                                                        >
                                                            <option value="">Firma seçiniz</option>
                                                            {companies.map((company) => (
                                                                <option key={company.id} value={company.id}>
                                                                    {company.companyName}
                                                                </option>
                                                            ))}
                                                        </Input>
                                                    </FormGroup>
                                                </Col>
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">Taksit Grubu</Label>
                                                        <Input
                                                            type="select"
                                                            name="accountGroupId"
                                                            value={form.accountGroupId}
                                                            onChange={handleChange}
                                                            className="rounded-3"
                                                            disabled={!form.companyId || loadingGroups}
                                                        >
                                                            <option value="">Grup seçiniz</option>
                                                            {groups.map(g => (
                                                                <option key={g.id} value={g.id}>
                                                                    {g.groupName}
                                                                </option>
                                                            ))}
                                                        </Input>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        </div>

                                        {/* Kimlik ve Vergi Bilgileri */}
                                        <div className="border rounded-4 p-4 mb-4" style={{ backgroundColor: '#f9f9f9' }}>
                                            <h6 className="mb-3 fw-semibold">Kimlik ve Vergi Bilgileri</h6>
                                            <Row className="g-3">
                                                {isSoleProp ? (
                                                    // Şahıs Şirketi ise sadece TC göster
                                                    <Col md={6}>
                                                        <FormGroup>
                                                            <Label className="small text-muted mb-1">TC Kimlik No</Label>
                                                            <Input
                                                                name="nationalId"
                                                                value={form.nationalId}
                                                                onChange={handleChange}
                                                                maxLength={11}
                                                                inputMode="numeric"
                                                                pattern="[0-9]*"
                                                                onInput={(e) => (e.target.value = e.target.value.replace(/\D/g, ""))}
                                                                className="rounded-3"
                                                                placeholder="11 haneli TC kimlik numarası"
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                ) : (
                                                    // Diğer tipler için sadece VKN göster
                                                    <Col md={6}>
                                                        <FormGroup>
                                                            <Label className="small text-muted mb-1">VKN (Vergi Kimlik No)</Label>
                                                            <Input
                                                                name="vknNumber"
                                                                value={form.vknNumber}
                                                                onChange={handleChange}
                                                                maxLength={10}
                                                                inputMode="numeric"
                                                                pattern="[0-9]*"
                                                                onInput={(e) => (e.target.value = e.target.value.replace(/\D/g, ""))}
                                                                className="rounded-3"
                                                                placeholder="10 haneli vergi kimlik numarası"
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                )}
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">Vergi Dairesi</Label>
                                                        <Input
                                                            name="taxOffice"
                                                            value={form.taxOffice}
                                                            onChange={handleChange}
                                                            className="rounded-3"
                                                            placeholder="Vergi dairesi adı"
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        </div>

                                        {/* İletişim ve Adres */}
                                        <div className="border rounded-4 p-4 mb-4" style={{ backgroundColor: '#f9f9f9' }}>
                                            <h6 className="mb-3 fw-semibold">İletişim ve Adres Bilgileri</h6>
                                            <Row className="g-3">
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <PhoneInput
                                                            value={form.phoneNumber}
                                                            onChange={handleChange}
                                                            name="phoneNumber"
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col md={3}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">Ülke</Label>
                                                        <Input
                                                            name="country"
                                                            value={form.country}
                                                            onChange={handleChange}
                                                            className="rounded-3"
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col md={3}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">Şehir</Label>
                                                        <Input
                                                            type="select"
                                                            name="city"
                                                            value={form.city}
                                                            onChange={handleChange}
                                                            className="rounded-3"
                                                        >
                                                            <option value="">Seçiniz</option>
                                                            {turkishCities.map((city) => (
                                                                <option key={city} value={city}>
                                                                    {city}
                                                                </option>
                                                            ))}
                                                        </Input>
                                                    </FormGroup>
                                                </Col>
                                                <Col md={4}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">İlçe</Label>
                                                        <Input
                                                            type="select"
                                                            name="district"
                                                            value={form.district}
                                                            onChange={handleChange}
                                                            className="rounded-3"
                                                            disabled={!form.city}
                                                        >
                                                            <option value="">İlçe seçiniz</option>
                                                            {districtOptions.map((dist) => (
                                                                <option key={dist} value={dist}>
                                                                    {dist}
                                                                </option>
                                                            ))}
                                                        </Input>
                                                    </FormGroup>
                                                </Col>
                                                <Col md={8}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">Adres</Label>
                                                        <Input
                                                            type="textarea"
                                                            name="address"
                                                            value={form.address}
                                                            onChange={handleChange}
                                                            rows={2}
                                                            className="rounded-3"
                                                            placeholder="Detaylı adres bilgisi"
                                                        />
                                                    </FormGroup>
                                                </Col>
                                                <Col md={12}>
                                                    <FormGroup>
                                                        <Label className="small text-muted mb-1">Açıklama</Label>
                                                        <Input
                                                            type="textarea"
                                                            name="description"
                                                            value={form.description}
                                                            onChange={handleChange}
                                                            rows={3}
                                                            className="rounded-3"
                                                            placeholder="Cari/Bayi hakkında ek bilgiler"
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        </div>

                                        {/* Butonlar */}
                                        <div className="d-flex justify-content-end gap-2 mt-4">
                                            <button type="button" className="btn btn-light btn-rounded px-4" style={{ height: 34, fontSize: ".8rem" }} onClick={() => navigate(-1)} disabled={saving}>
                                                İptal
                                            </button>
                                            <button type="submit" className="btn btn-navy btn-rounded px-4 d-flex align-items-center gap-1" style={{ height: 34, fontSize: ".8rem" }} disabled={saving}>
                                                {saving ? (<><Spinner size="sm" className="me-1" /> Kaydediliyor...</>) : (<><i className="bx bx-save" style={{ fontSize: 16 }} /> Değişiklikleri Kaydet</>)}
                                            </button>
                                        </div>
                                    </Form>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </div>

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </React.Fragment>
    );
};

export default EditAccountPage;